import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import {
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  createApiRef,
} from '@backstage/core-plugin-api';
import { createApp } from '@backstage/frontend-defaults';
import {
  ApiBlueprint,
  PageBlueprint,
  configApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityIconLinkBlueprint } from '@backstage/plugin-catalog-react/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid/alpha';
import GitHubIcon from '@material-ui/icons/GitHub';
import { Navigate } from 'react-router-dom';

/** Frontend API for auth.providers.oidc (Okta). Provider id must stay `oidc`. */
const oktaAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({
  id: 'auth.okta',
});

const oktaAuthApi = ApiBlueprint.make({
  name: 'okta',
  params: defineParams =>
    defineParams({
      api: oktaAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            id: 'oidc',
            title: 'Okta',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});

const githubSourceLink = EntityIconLinkBlueprint.make({
  name: 'github-source',
  params: {
    useProps() {
      const { entity } = useEntity();
      const slug = entity.metadata.annotations?.['github.com/project-slug'];
      const href =
        entity.metadata.annotations?.['backstage.io/view-url'] ||
        (slug ? `https://github.com/${slug}` : undefined);
      return {
        label: 'View Source',
        title: 'Open repository on GitHub',
        icon: <GitHubIcon />,
        href,
        disabled: !href,
      };
    },
  },
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => (
      <SignInPage
        {...props}
        // Single provider + auto: skip the extra button click (Okta tile / deep link).
        auto
        provider={{
          id: 'okta-auth-provider',
          title: 'Okta',
          message: 'Signing in with Okta…',
          apiRef: oktaAuthApiRef,
        }}
      />
    ),
  },
});

// No homepage plugin — `/` otherwise renders Backstage's NotFound ("dropped the mic").
// Okta's tile also lands on login_uri `/`; send both to Catalog (triggers auto Okta).
const homeRedirectPage = PageBlueprint.make({
  name: 'home-redirect',
  params: {
    path: '/',
    loader: async () => <Navigate to="/catalog" replace />,
  },
});

export default createApp({
  features: [
    catalogPlugin,
    scaffolderPlugin,
    techdocsPlugin,
    techDocsMermaidAddonModule,
    createFrontendModule({
      pluginId: 'app',
      extensions: [oktaAuthApi, signInPage, githubSourceLink, homeRedirectPage],
    }),
  ],
});
